<template>
    <div class="ds-ex-tab">
        <RequestQuickForm v-if="canCreate" @created="load('action')" />

        <div class="ds-ex-toolbar">
            <div class="ds-glass-segment ds-ex-segment" role="group" aria-label="Направление запросов">
                <button type="button" class="ds-glass-segment-option" :class="{ 'is-active': f.direction === 'incoming' }" :aria-pressed="f.direction === 'incoming'" @click="f.direction = 'incoming'">
                    Входящие
                    <span v-if="incomingPending !== null" class="ds-ex-count" :class="{ 'is-dark': incomingPending > 0 }">{{ incomingPending }}</span>
                </button>
                <button type="button" class="ds-glass-segment-option" :class="{ 'is-active': f.direction === 'outgoing' }" :aria-pressed="f.direction === 'outgoing'" @click="f.direction = 'outgoing'">
                    Мои запросы
                    <span v-if="outgoingCount !== null" class="ds-ex-count">{{ outgoingCount }}</span>
                </button>
            </div>
            <span class="ds-ex-divider" aria-hidden="true"></span>
            <ExchangePeriodFilter v-model="f.period" />
            <ExchangeOptionFilter v-model="lifecycleModel" label="Статус" multiple :options="LIFECYCLE_OPTIONS" />
            <EmployeeFilter v-model="f.positionIds" label="Должность" search-placeholder="Найти должность" :options="positionOptions" />
            <div class="ds-ex-spacer"></div>
            <ExchangeSearch v-model="f.search" />
        </div>

        <ExchangeFilterChips v-if="chips.length" :chips="chips" :found="summary?.matched_count ?? null" :total="summary?.total_in_period ?? null" @remove="removeChip" @reset="resetFilters" />

        <div v-if="backgroundError" class="ds-ex-bg-error" role="status">
            <span>Не удалось обновить данные</span>
            <button type="button" class="ds-ex-link-btn" @click="load('background')">Повторить</button>
        </div>

        <div class="ds-ex-table" role="table" aria-label="Запросы" :aria-busy="status === 'loading'">
            <div class="ds-ex-grid ds-ex-head ds-ex-grid-requests" role="row">
                <template v-if="f.direction === 'incoming'">
                    <span>Когда</span><span>Кто просит</span><span>Ваш сотрудник</span><span>Должность</span><span>Статус</span><span class="ds-ex-right">Ответ</span>
                </template>
                <template v-else>
                    <span>Когда</span><span>Из магазина</span><span>Сотрудник</span><span>Должность у вас</span><span>Статус</span><span class="ds-ex-right">Действие</span>
                </template>
            </div>
            <template v-if="status === 'loading'">
                <div v-for="index in 5" :key="index" class="ds-ex-grid ds-ex-row ds-ex-grid-requests" aria-hidden="true">
                    <span class="ds-ex-skeleton w-[90px]"></span>
                    <span class="ds-ex-skeleton w-[150px]"></span>
                    <span class="ds-ex-skeleton w-[160px]"></span>
                    <span class="ds-ex-skeleton w-[120px]"></span>
                    <span class="ds-ex-skeleton w-[80px]"></span>
                    <span class="ds-ex-skeleton w-[120px] ml-auto"></span>
                </div>
            </template>
            <div v-else-if="status === 'error'" class="ds-ex-state" role="alert">
                <span>Не удалось загрузить запросы</span>
                <button type="button" class="ds-glass-btn ds-glass-btn-secondary" @click="load('reset')">Повторить</button>
            </div>
            <div v-else-if="!items.length" class="ds-ex-state">
                <template v-if="hasConditions">
                    <span>Ничего не нашли</span>
                    <button type="button" class="ds-glass-btn ds-glass-btn-secondary" @click="resetFilters">Сбросить всё</button>
                </template>
                <span v-else>{{ f.direction === 'incoming' ? 'Входящих запросов за период нет' : 'Запросов за период нет' }}</span>
            </div>
            <template v-else>
                <template v-for="entry in entries" :key="entry.key">
                    <div v-if="entry.type === 'group'" class="ds-ex-group">{{ entry.label }}</div>
                    <RequestRow
                        v-else
                        :request="entry.request"
                        :incoming="isIncoming(entry.request)"
                        :other-store-name="otherStoreName(entry.request)"
                        :busy="actions.isBusy(entry.request.id)"
                        :can-approve="canApprove"
                        :can-manage="canManageOwn"
                        :now="now"
                        @approve="actions.approveRequest(entry.request)"
                        @reject="actions.rejectRequest(entry.request)"
                        @ask-other="askOther(entry.request)"
                        @history="emit('open', entry.request, 'history')"
                        @remove="emit('remove', entry.request)"
                    />
                </template>
            </template>
        </div>

        <Pager v-if="status !== 'error'" :total="count" v-model:page="f.page" v-model:page-size="f.pageSize" />
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, toRef, watch } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import EmployeeFilter from '@/components/employees/EmployeeFilter.vue'
import Pager from '@/components/employees/Pager.vue'
import ExchangeFilterChips, { type ExchangeChip } from './ExchangeFilterChips.vue'
import ExchangeOptionFilter from './ExchangeOptionFilter.vue'
import ExchangePeriodFilter from './ExchangePeriodFilter.vue'
import ExchangeSearch from './ExchangeSearch.vue'
import RequestQuickForm from './RequestQuickForm.vue'
import RequestRow from './RequestRow.vue'
import { useExchangeActions } from './exchangeActions'
import { useExchangeDirectory } from './exchangeDirectory'
import { useExchangePage } from './exchangePage'
import { useExchangeList } from './useExchangeList'
import { dayKeyOf, REQUEST_LIFECYCLE_LABELS, wallNow, workTime } from './exchangeFormat'
import type { ListQuery, RequestLifecycle, Vacancy } from './exchangeTypes'

const props = defineProps<{ active: boolean }>()
const emit = defineEmits<{ open: [vacancy: Vacancy, tab: 'vacancy' | 'history'], remove: [vacancy: Vacancy] }>()

const LIFECYCLE_OPTIONS = (Object.keys(REQUEST_LIFECYCLE_LABELS) as RequestLifecycle[]).map(id => ({ id, name: REQUEST_LIFECYCLE_LABELS[id] }))

const authStore = useAuthStore()
const page = useExchangePage()
const f = page.requests
const directory = useExchangeDirectory()

const canCreate = computed(() => Boolean(authStore.permissions?.can_create_vacancy_request))
// Право согласования входящего — как у прежнего списка запросов.
const canApprove = computed(() => Boolean(authStore.permissions?.can_confirm_vacancy_request))
// DELETE /api/vacancy/{id}/ — серверное право can_create_vacancy_exchange (spec, таблица API).
const canManageOwn = computed(() => Boolean(authStore.permissions?.can_create_vacancy_exchange))
const positionOptions = computed(() => directory.positions.value)

const query = computed<ListQuery>(() => ({
    page: f.page,
    pageSize: f.pageSize,
    period: f.period,
    search: f.search,
    positionIds: f.positionIds,
    isUrgent: null,
    exchangeType: null,
    direction: f.direction,
    lifecycle: f.lifecycle,
}))

// Объявлено до списка: сброс страницы применяется в том же цикле, без второго запроса.
watch(() => [f.direction, f.period.join(), f.lifecycle.join(), f.positionIds.join(), f.search, f.pageSize], () => { f.page = 1 })

const { items, count, status, backgroundError, summary, load } = useExchangeList('requests', query, {
    active: toRef(props, 'active'),
    onPageOverflow: last => { f.page = last },
})
const actions = useExchangeActions(() => load('action'))

// Настенное «сейчас» магазина — для «Горящая» и запрета действий с начала смены.
const now = ref(wallNow())
const clock = setInterval(() => { now.value = wallNow() }, 60_000)
onBeforeUnmount(() => clearInterval(clock))

const lifecycleModel = computed<string[]>({
    get: () => f.lifecycle,
    set: value => { f.lifecycle = value as RequestLifecycle[] },
})

const incomingPending = computed(() => summary.value?.segments?.incoming_pending ?? null)
const outgoingCount = computed(() => summary.value?.segments?.outgoing ?? null)
const hasConditions = computed(() => Boolean(f.lifecycle.length || f.positionIds.length || f.search))

const profileId = computed(() => directory.profileDepartment.value?.id ?? null)
const isIncoming = (request: Vacancy) => Boolean(profileId.value && request.requestedDepartmentId === profileId.value)
const otherStoreName = (request: Vacancy) => request.requestedDepartmentName
    ?? directory.departments.value.find(item => item.id === request.requestedDepartmentId)?.name
    ?? '—'

const isPending = (request: Vacancy) => !(request.lifecycle === 'rejected' || request.isRequestRejected || request.lifecycle === 'closed' || (!request.lifecycle && request.status === 'completed'))

type Entry = { type: 'group', key: string, label: string } | { type: 'row', key: string, request: Vacancy }

// Входящие: «Нужен ваш ответ» выше «Отвечено за период» — порядок сервера, заголовки без перестановки.
const entries = computed<Entry[]>(() => {
    const result: Entry[] = []
    let last = ''
    for (const request of items.value) {
        if (f.direction === 'incoming' && isIncoming(request)) {
            const group = isPending(request) ? 'pending' : 'answered'
            if (group !== last) {
                const total = summary.value?.segments?.[group === 'pending' ? 'incoming_pending' : 'incoming_answered']
                const label = group === 'pending' ? 'НУЖЕН ВАШ ОТВЕТ' : 'ОТВЕЧЕНО ЗА ПЕРИОД'
                result.push({ type: 'group', key: `group:${group}`, label: total === undefined ? label : `${label} · ${total}` })
                last = group
            }
        }
        result.push({ type: 'row', key: request.id, request })
    }
    return result
})

const chips = computed<ExchangeChip[]>(() => [
    ...f.lifecycle.map(id => ({ key: `lifecycle:${id}`, label: REQUEST_LIFECYCLE_LABELS[id] })),
    ...f.positionIds.map(id => ({ key: `position:${id}`, label: directory.positions.value.find(item => item.id === id)?.name ?? 'Должность' })),
])

function removeChip(key: string) {
    if (key.startsWith('lifecycle:')) f.lifecycle = f.lifecycle.filter(id => `lifecycle:${id}` !== key)
    else if (key.startsWith('position:')) f.positionIds = f.positionIds.filter(id => `position:${id}` !== key)
}

function resetFilters() {
    f.lifecycle = []
    f.positionIds = []
    f.search = ''
}

// «Запросить другого»: прежние дата/время/должность/магазин, сотрудник очищен; старая запись не меняется.
function askOther(request: Vacancy) {
    const draft = page.requestDraft
    draft.date = dayKeyOf(request.start)
    draft.start = workTime(request.start).format('HH:mm')
    draft.end = workTime(request.end).format('HH:mm')
    draft.positionId = request.position.id
    draft.fromDepartment = request.requestedDepartmentId ? { id: request.requestedDepartmentId, name: otherStoreName(request) } : null
    draft.employeeId = ''
    window.scrollTo({ top: 0, behavior: 'smooth' })
}

defineExpose({ refresh: () => load('action') })
</script>
